import {
  getShippingAddress, getBillingAddress, cleanUpAddressValues, getStateForCountry,
} from '../../../tools/address';
import { buildCreditCard, isCreditCardInfoComplete, isSavedCardInfoComplete } from '../../../tools/creditCard';
import { encrypt } from '../../../tools/encryption/encryption';
import { getToken } from '../../../tools/pxp';
import { supplementMarketingInfo } from '../../../tools/marketingInfo';
import { supplementPltvInfo } from '../../../tools/pltvInfo';
import { getSignifydSessionId } from '../../../tools/signifydSession';

export async function prepareCreditCardData(formData, pxpConfig) {
  const cardPayments = [];
  if (formData?.payment?.cardPayments?.length) {
    const cardPayment = formData?.payment?.cardPayments[0];
    const creditCard = buildCreditCard(cardPayment);
    if (isCreditCardInfoComplete(creditCard)) {
      const encryptedCardNumber = encrypt({
        value: creditCard?.cardNumber,
        maxRetries: pxpConfig?.pxpEncryptRetries,
        convertToHex: true,
        exponent: pxpConfig?.publicEncryptionKey?.rsa?.exponent,
        modulus: pxpConfig?.publicEncryptionKey?.rsa?.modulus,
      });
      const pxpToken = await getToken(
        encryptedCardNumber,
        creditCard?.cardExpireMonth,
        creditCard?.cardExpireYear,
        pxpConfig,
      );
      cardPayments.push({
        ...creditCard,
        cardNumber: pxpToken ?? encryptedCardNumber,
        cardNumberType: pxpToken ? 'singleUseToken' : 'encryptedCardNumber',
        encryptionId: pxpConfig?.publicEncryptionKey?.id || '',
      });
    } else if (isSavedCardInfoComplete(cardPayment)) {
      cardPayments.push({
        ...cardPayment,
      });
    }
  }
  return cardPayments;
}

export async function preparePaymentData(formData, pxpConfig, options = {}) {
  let payment = { ...(formData?.payment ?? {}) };
  const { paymentType, paymentCode } = options || {};

  // For any non-credit-card selection, drop card details.
  // If PayPal is selected, also ensure paypalPayment.paymentType = 'regular'.
  // If APM payment is selected, ensure apmPayment structure is correct.
  // If COD payment is selected, ensure codPayment structure is correct.
  // if paymentType and it's not credit card, drop card details
  if (paymentType && paymentType !== 'credit card') {
    if (payment?.cardPayments) delete payment.cardPayments;
    if (paymentType === 'paypal') {
      payment = {
        ...payment,
        paypalPayment: { ...(payment?.paypalPayment ?? {}), paymentType: 'regular' },
      };
    } else if (paymentType === 'apm') {
      // Handle APM payments (Alternative Payment Methods)
      // Use paymentCode if available, otherwise fall back to paymentType
      payment = {
        ...payment,
        apmPayment: { paymentCode: paymentCode || paymentType },
      };
    } else if (paymentType === 'cod') {
      // Handle COD (Cash on Delivery) payments
      payment = {
        ...payment,
        codPayment: { paymentCode: paymentCode || paymentType },
      };
    }
    return payment;
  }

  // prepare card details if provided
  if (payment?.cardPayments) {
    const cardPayments = await prepareCreditCardData(formData, pxpConfig);
    payment = { cardPayments };
  }
  return payment;
}

export default async function prepareOrderSubmitData(
  formData,
  checkoutPageState = {},
  options = {},
) {
  const {
    shipMethod,
    shippingSpeed = [],
    shippingAddress,
    orderContact,
    pxpConfig,
    bagItems,
    brand,
    paymentOptions = [],
  } = checkoutPageState;
  const { paymentType: paymentTypeFromOpts } = options || {};
  const selectedPaymentType = paymentTypeFromOpts
    || checkoutPageState?.selectedPaymentType
    || '';

  // Find the selected payment option to get its code
  const selectedPaymentOption = paymentOptions?.find(
    (paymentOption) => paymentOption?.type?.toLowerCase() === selectedPaymentType,
  );
  const paymentCode = selectedPaymentOption?.code || selectedPaymentType;

  const hasOnlyEGiftCard = bagItems?.hasOnlyEGiftCard;
  const {
    maxEstimatedDeliveryDate,
    minEstimatedDeliveryDate,
    minEstimatedDeliveryDateDisplay,
    displayEDDRange,
    showMaxEstimatedDeliveryDate,
  } = shippingSpeed.find(
    ({ shipModeId }) => shipModeId === shipMethod?.shipModeId,
  ) || {};
  const payment = await preparePaymentData(
    formData,
    pxpConfig,
    { paymentType: selectedPaymentType, paymentCode },
  );
  const countriesConfig = checkoutPageState?.orderSubmitLegalTerms?.countriesConfig;

  // Helper to normalize state value based on country configuration
  const normalizeState = (address) => {
    if (!address) return address;
    // Only normalize when we have countries configuration; otherwise preserve provided state
    if (!countriesConfig) return address;
    const countryCode = address?.country;
    const stateValue = address?.state || countryCode;
    return {
      ...address,
      state: getStateForCountry(countryCode, stateValue, countriesConfig),
    };
  };

  const shipToAddress = !hasOnlyEGiftCard ? (() => {
    const currentAddress = formData?.shippingAddress ?? getShippingAddress(
      formData,
      shippingSpeed,
      shippingAddress,
      orderContact,
    );
    return normalizeState(cleanUpAddressValues(currentAddress));
  })() : null;
  // The EDD range is shown to the user in the shipping speed options
  // only when the following condition is met.
  const shouldIncludeEDDRangeInCartPost = displayEDDRange
        && showMaxEstimatedDeliveryDate
        && minEstimatedDeliveryDateDisplay;
  return {
    billingAddressSameAsShipping: formData?.billingAddressSameAsShipping ?? false,
    shippingAddressSameAsBilling: formData?.shippingAddressSameAsBilling ?? false,
    estimatedDeliveryDate: formData?.estimatedDeliveryDate ?? '',
    estimatedShipDate: formData?.estimatedShipDate ?? '',
    items: formData?.items ?? [],
    payment,
    shippingAddress: shipToAddress,
    billingAddress: (() => {
      const currentAddress = formData?.billingAddress ?? getBillingAddress(formData, shippingSpeed);
      return normalizeState(cleanUpAddressValues(currentAddress));
    })(),
    shipModeId: formData?.shipModeId ?? '',
    signifydSessionId: (formData?.signifydSessionId && formData.signifydSessionId.trim() !== '')
      ? formData.signifydSessionId
      : (() => {
        try {
          return getSignifydSessionId() ?? '';
        } catch (error) {
          return '';
        }
      })(),
    smsOptIn: formData?.smsOptIn ?? false,
    valueTierFlag: formData?.valueTierFlag ?? supplementPltvInfo({}, { brand })?.valueTierFlag ?? '',
    marketingInfo: formData?.marketingInfo
      ?? (supplementMarketingInfo({}, { brand })?.marketingInfo ?? {}),
    ...(shouldIncludeEDDRangeInCartPost ? {
      minEstimatedDeliveryDate,
      maxEstimatedDeliveryDate,
    } : {}),
  };
}
